import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Idle timeouts per decision P4 (tenant- and role-configurable; these are the defaults). The server-side session
 * policy (max 12 h, refresh-token revocation) is enforced by Keycloak/API later; this hook only locks the UI.
 */
export const idleTimeoutDefaults = {
  backOffice: 30 * 60_000,
  pos: 5 * 60_000,
} as const;

const activityEvents = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"] as const;

export interface IdleLockOptions {
  /** Inactivity in milliseconds before the screen locks; `0` or less disables locking. */
  timeoutMs: number;
  /** Called once when the screen locks (e.g. to drop cached sensitive data). */
  onLock?: () => void;
  /** Element to watch; defaults to the window. */
  target?: Window | HTMLElement;
}

export interface IdleLockState {
  locked: boolean;
  /** Unlocks and restarts the timer (re-authentication / PIN check happens before calling this). */
  unlock: () => void;
  /** Locks immediately (e.g. "Lock screen" in the user menu). */
  lock: () => void;
}

/**
 * Locks the UI after a period without user activity. Activity events are cheap to handle: they only record a
 * timestamp; a single timer checks the deadline. While locked, activity does not unlock. Hidden tabs keep counting.
 */
export function useIdleLock({ timeoutMs, onLock, target }: IdleLockOptions): IdleLockState {
  const [locked, setLocked] = useState(false);
  const lastActivity = useRef(Date.now());
  const lockedRef = useRef(false);
  const onLockRef = useRef(onLock);

  useEffect(() => {
    onLockRef.current = onLock;
  }, [onLock]);

  const lock = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    onLockRef.current?.();
  }, []);

  const unlock = useCallback(() => {
    lockedRef.current = false;
    lastActivity.current = Date.now();
    setLocked(false);
  }, []);

  useEffect(() => {
    if (timeoutMs <= 0) return undefined;
    const element = target ?? window;
    const onActivity = () => {
      if (!lockedRef.current) lastActivity.current = Date.now();
    };
    for (const name of activityEvents) element.addEventListener(name, onActivity, { passive: true, capture: true });
    lastActivity.current = Date.now();

    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      const remaining = lastActivity.current + timeoutMs - Date.now();
      if (remaining <= 0 && !lockedRef.current) {
        lock();
        return;
      }
      timer = setTimeout(schedule, Math.max(remaining, 1000));
    };
    timer = setTimeout(schedule, timeoutMs);

    return () => {
      clearTimeout(timer);
      for (const name of activityEvents) element.removeEventListener(name, onActivity, { capture: true });
    };
  }, [lock, locked, target, timeoutMs]);

  return { locked, unlock, lock };
}
